import { useState } from 'react'
import { api } from '../../../lib/api.js'
import { runAssignmentMap } from '../../../lib/assignmentMapRun.js'
import { downloadCsv } from '../../../lib/csvExport.js'
import { needsAssignment } from '../assignments.js'
import styles from './assignments.module.css'

const csvCell = value => `"${String(value ?? '').replaceAll('"', '""')}"`

export default function RuleAssignments({ items, complete, onVerified, call = api.assignmentsApplySelected }) {
  const [reason, setReason] = useState('Fill blank championship assignments using the established assignment map; preserve human owners, locks, roster overrides and scoped rules.')
  const [preview, setPreview] = useState(null)
  const [result, setResult] = useState(null)
  const [progress, setProgress] = useState(null)
  const [busy, setBusy] = useState(false)
  const [limit, setLimit] = useState(25)
  const records = items.filter(needsAssignment)
  async function run(dryRun) {
    setBusy(true); setProgress(null)
    try {
      const receipt = await runAssignmentMap({ records, call,
        reason, dryRun, unassignedOnly: true, preview,
        onProgress: setProgress, onVerified })
      setResult(receipt)
      setPreview(dryRun && receipt.ok ? receipt : null)
    } finally { setBusy(false) }
  }
  function exportAudit() {
    const rows = (result?.decisions || []).map(row => [row.id, row.name, row.body, row.conference,
      row.current, row.proposed, row.outcome, row.reason].map(csvCell).join(','))
    downloadCsv('sr-assignment-map-audit.csv', ['Record ID,Championship,Governing body,Conference,Previous owner,Proposed owner,Outcome,Rule or next decision', ...rows].join('\r\n'))
  }
  const decisions = result?.decisions || []
  return <section className={styles.flags} aria-label="Established assignment rules">
    <h3>Apply established assignment rules</h3>
    <p>Preview the {records.length.toLocaleString()} unassigned championships in this filtered scope against the current roster and assignment map. Retired records stay excluded. Win-history suggestions below are separate proposals.</p>
    <label style={{ display: 'grid', gap: 6 }}>Assignment reason <input style={{ width: '100%', minWidth: 0, boxSizing: 'border-box' }} value={reason} disabled={busy} onChange={event => { setReason(event.target.value); setPreview(null) }} /></label>
    <p>Keep this view open while the batches run. Each verified assignment is durable and has its own audit trail.</p>
    <button className={styles.moreBtn} disabled={busy || !complete || !records.length || !reason.trim()} onClick={() => run(true)}>Preview established rules</button>
    {preview?.planned > 0 && <button className={styles.moreBtn} disabled={busy} onClick={() => run(false)}>Apply {preview.planned.toLocaleString()} prepared assignments</button>}
    {!complete && <p>The full championship feed must finish loading before a preview can run.</p>}
    {progress && <p role="status">Reviewed {progress.done.toLocaleString()} of {progress.total.toLocaleString()}; {progress.verified.toLocaleString()} saved and verified.</p>}
    {result && <><p role="status">{result.note}</p>
      {result.uncertainIds.length > 0 && <p role="alert">Inspect these records before retrying: {result.uncertainIds.join(', ')}.</p>}
      <button className={styles.moreBtn} onClick={exportAudit}>Download assignment audit</button>
      <p>Showing {Math.min(limit, decisions.length).toLocaleString()} of {decisions.length.toLocaleString()} decisions. Proposed means no write; verified means saved and read back.</p>
      <ol>{decisions.slice(0, limit).map(row => <li key={row.id}>{row.name || row.id}: {row.proposed || row.current || 'Needs assignment rule'} · {row.outcome} · {row.reason}</li>)}</ol>
      {decisions.length > limit && <button className={styles.moreBtn} onClick={() => setLimit(limit + 50)}>Show more decisions</button>}
    </>}
  </section>
}
